import { FC } from 'react';
import { useMutation } from '@apollo/client';

import { IntraCompanyFundsTransferStatus } from '__generated__/GQL';
import useAuthenticatedAction from 'common/hooks/useAuthenticatedAction';
import { Scope } from 'common/hooks/useAuthenticatedAction/types';
import useCompanyContext from 'common/hooks/useCompanyContext';
import { StepProps } from 'components/Stepper/types';
import TrackView from 'features/Analytics/TrackView';
import {
  BankTransferCreationStep,
  useBankTransferContext,
} from 'features/Payments/Pages/Transfers/contexts';

import PinCode from '../../components/PinCode';
import { CreateIntraCompanyFundsTransferDocument } from './graphql/createIntraCompanyFundsTransfer.gql';

const isFailureStatus = (status?: IntraCompanyFundsTransferStatus) =>
  status &&
  [
    IntraCompanyFundsTransferStatus.Canceled,
    IntraCompanyFundsTransferStatus.RejectedByProvider,
  ].includes(status);

const PerformInternalTransfer: FC<StepProps<BankTransferCreationStep>> = (
  props,
) => {
  const { companyProfileId, companyUserId } = useCompanyContext();

  if (!companyProfileId) {
    throw new Error(
      'companyProfileId is required to create an internal transfer',
    );
  }
  if (!companyUserId) {
    throw new Error('companyUserId is required to create an internal transfer');
  }

  const {
    bankTransfer: { amount, fromBankAccountId, label, toBankAccountId },
  } = useBankTransferContext('performInternal');

  const [
    createIntraCompanyFundsTransfer,
    { loading: createIntraCompanyFundsTransferLoading },
  ] = useMutation(CreateIntraCompanyFundsTransferDocument);

  const { isLoading: isAuthenticating, onPasscodeSubmit } =
    useAuthenticatedAction({
      onPasscodeSuccess: async (token) => {
        try {
          const { data } = await createIntraCompanyFundsTransfer({
            context: {
              debounceKey: 'createInternalBankTransfer',
              headers: {
                'short-lived-token': token,
              },
            },
            variables: {
              input: {
                amount,
                companyProfileId: companyProfileId,
                companyUserId: companyUserId,
                fromBankAccountId,
                toBankAccountId,
                userLabel: label,
              },
            },
          });

          if (isFailureStatus(data?.createIntraCompanyFundsTransfer.status)) {
            props.goNext({
              key: 'failure',
              shouldReplaceCurrentStep: true,
            });
          } else {
            props.goNext();
          }
        } catch {
          props.goNext({
            key: 'failure',
            shouldReplaceCurrentStep: true,
          });
        }
      },
      scope: Scope.BANK_TRANSFERS_WRITE,
    });

  const loading = isAuthenticating || createIntraCompanyFundsTransferLoading;

  return (
    <>
      <TrackView pageName="PerformNewInternalTransfer" />
      <PinCode
        goBack={props.goBack}
        loading={loading}
        onPasscodeSubmit={onPasscodeSubmit}
      />
    </>
  );
};

export default PerformInternalTransfer;
